import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollText, Users, AlertTriangle, Loader2 } from "lucide-react";

export const LegalTab = () => {
  const [info, setInfo] = useState(null);
  const [version, setVersion] = useState("");
  const [effective, setEffective] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/terms-version");
      setInfo(data);
      setVersion(data.version || "");
      setEffective(data.effective_date || "");
    } catch {
      toast.error("Couldn't load Terms version");
    }
  };
  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!version.trim()) return toast.error("Enter a version label.");
    if (info && version.trim() === info.version) {
      return toast.error("That's already the current version — bump it to prompt a re-accept.");
    }
    if (!window.confirm(`Publish Terms version "${version.trim()}"? Every user not on this version will be asked to re-accept on their next visit.`)) return;
    setBusy(true);
    try {
      const { data } = await api.put("/admin/terms-version", { version: version.trim(), effective_date: effective || undefined });
      setInfo(data);
      toast.success(`Published version ${data.version} — ${data.users_pending} user(s) will be asked to re-accept.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't publish version");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="legal-tab">
      <div className="rounded-xl border border-black/10 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><ScrollText className="h-4.5 w-4.5" /></div>
          <div className="flex-1">
            <p className="font-heading text-base" style={{ fontWeight: 700 }}>Terms &amp; Privacy version</p>
            <p className="text-xs text-muted-foreground">
              After you edit the Terms of Service or Privacy Policy text, publish a new version here.
              Everyone who accepted an older version will be prompted to re-accept before they can keep using BogoShare.
            </p>

            {info && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current version</p>
                  <p className="font-heading text-lg" data-testid="legal-current-version" style={{ fontWeight: 700 }}>{info.version}</p>
                  <p className="text-[11px] text-muted-foreground">Effective {info.effective_date}</p>
                </div>
                <div className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">On current</p>
                  <p className="font-heading text-lg" style={{ fontWeight: 700 }}>{info.users_on_current}<span className="text-sm text-muted-foreground">/{info.users_total}</span></p>
                  <p className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Users className="h-3 w-3" /> users accepted</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-amber-700">Pending re-accept</p>
                  <p className="font-heading text-lg text-amber-800" data-testid="legal-pending-count" style={{ fontWeight: 700 }}>{info.users_pending}</p>
                  <p className="text-[11px] text-amber-700">will be prompted</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-black/10 bg-white p-5">
        <p className="font-heading text-base mb-1" style={{ fontWeight: 700 }}>Publish a new version</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Version label</Label>
            <Input data-testid="legal-version-input" value={version} onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 1.1" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Effective date</Label>
            <Input data-testid="legal-effective-input" type="date" value={effective}
              onChange={(e) => setEffective(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Publishing signs out everyone from the "accepted" state and shows them a re-accept prompt on their next action.
        </div>
        <Button data-testid="legal-publish-btn" onClick={publish} disabled={busy} className="mt-3 rounded-full">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Publish new version
        </Button>
      </div>
    </div>
  );
};

export default LegalTab;
